import { useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";
import { WifiOff, RefreshCw, Bell, X } from "lucide-react";
import { Button } from "../ui/button";

export function PWAProvider({ children }) {
  const [offline, setOffline] = useState(!navigator.onLine);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updateSW, setUpdateSW] = useState(null);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);

  useEffect(() => {
    const swUpdate = registerSW({
      onNeedRefresh() {
        setNeedRefresh(true);
      },
      onOfflineReady() {
        // App is ready for offline use — no-op, we show offline banner reactively
      }
    });
    setUpdateSW(() => swUpdate);

    function handleOnline() { setOffline(false); }
    function handleOffline() { setOffline(true); }
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check if we should prompt for notifications (only on mobile, after first visit)
    if ("Notification" in window && Notification.permission === "default") {
      const dismissed = sessionStorage.getItem("clyntr_notif_dismissed");
      if (!dismissed) {
        // Delay prompt so it doesn't interrupt initial load
        const timer = setTimeout(() => setShowNotifPrompt(true), 5000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener("online", handleOnline);
          window.removeEventListener("offline", handleOffline);
        };
      }
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  async function handleEnableNotifications() {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        new Notification("Clyntr.ai", {
          body: "Notifications enabled! You'll be alerted about approvals and posting status.",
          icon: "/favicon.svg"
        });
      }
    } catch {
      // Browser doesn't support notifications
    }
    setShowNotifPrompt(false);
  }

  function dismissNotifPrompt() {
    sessionStorage.setItem("clyntr_notif_dismissed", "1");
    setShowNotifPrompt(false);
  }

  return (
    <>
      {children}

      {/* Offline Banner */}
      {offline && (
        <div className="fixed bottom-16 md:bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full border border-warning/30 bg-warning/10 backdrop-blur px-4 py-2 shadow-lg">
          <WifiOff size={16} className="text-warning shrink-0" />
          <span className="text-xs font-medium text-foreground">You're offline — drafts will sync when connected</span>
        </div>
      )}

      {/* Update Available Banner */}
      {needRefresh && (
        <div className="fixed bottom-16 md:bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 backdrop-blur px-4 py-2 shadow-lg">
          <RefreshCw size={16} className="text-primary shrink-0" />
          <span className="text-xs font-medium text-foreground">Update available</span>
          <Button
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => { if (updateSW) updateSW(true); }}
          >
            Refresh
          </Button>
        </div>
      )}

      {/* Notification Permission Prompt — mobile-friendly bottom card */}
      {showNotifPrompt && (
        <div className="fixed bottom-16 md:bottom-4 left-2 right-2 md:left-auto md:right-4 md:w-80 z-[60] rounded-xl border border-border bg-card p-3 shadow-xl">
          <div className="flex items-start gap-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10">
              <Bell size={16} className="text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Enable Notifications</p>
              <p className="text-xs text-muted-foreground mt-0.5">Get alerts for bill approvals, posting status, and compliance deadlines.</p>
              <div className="mt-2 flex items-center gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={handleEnableNotifications}>Enable</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={dismissNotifPrompt}>Not now</Button>
              </div>
            </div>
            <button className="shrink-0 text-muted-foreground" onClick={dismissNotifPrompt}>
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
